import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

interface BookingStatus {
  id: number;
  status: 'pending_schedule' | 'pending_payment' | 'scheduled' | 'in_progress' | 'completed' | 'cancelled';
  customer_name: string;
  service_type: string;
  plant_count: string;
  address: string;
  monthly_price: number;
  created_at: string;
  stripe_session_id?: string;
}

interface BookingStatusData {
  bookings: BookingStatus[];
  isLoading: boolean;
  error: string | null;
  refreshBookings: () => Promise<void>;
  updateBookingStatus: (bookingId: number, newStatus: BookingStatus['status']) => Promise<void>;
}

export function useBookingStatus(): BookingStatusData {
  const [bookings, setBookings] = useState<BookingStatus[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  const fetchBookings = useCallback(async () => {
    if (!user) {
      setBookings([]);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('bookings')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (fetchError) {
        throw fetchError;
      }

      setBookings((data || []) as BookingStatus[]);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch bookings';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  const updateBookingStatus = useCallback(async (bookingId: number, newStatus: BookingStatus['status']) => {
    try {
      const { error: updateError } = await supabase
        .from('bookings')
        .update({ status: newStatus })
        .eq('id', bookingId);

      if (updateError) {
        throw updateError;
      }

      // Update local state
      setBookings(prev => prev.map(booking => 
        booking.id === bookingId 
          ? { ...booking, status: newStatus }
          : booking
      ));

      toast.success('Booking status updated successfully');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update booking status';
      setError(errorMessage);
      toast.error(errorMessage);
    }
  }, []);

  // Set up real-time subscription for booking updates
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel('booking-updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'bookings',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          console.log('Real-time booking update:', payload);
          
          switch (payload.eventType) {
            case 'INSERT':
              setBookings(prev => [payload.new as BookingStatus, ...prev]);
              toast.success('New booking created!');
              break;
            case 'UPDATE':
              setBookings(prev => prev.map(booking => 
                booking.id === payload.new.id 
                  ? payload.new as BookingStatus
                  : booking
              ));
              toast.info(`Booking status updated to: ${payload.new.status}`);
              break;
            case 'DELETE':
              setBookings(prev => prev.filter(booking => booking.id !== payload.old.id));
              toast.info('Booking removed');
              break;
          }
        }
      )
      .subscribe();

    // Initial fetch
    fetchBookings();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, fetchBookings]);

  return {
    bookings,
    isLoading,
    error,
    refreshBookings: fetchBookings,
    updateBookingStatus
  };
}